import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "../../lib/brand";

const LINKS = [
  { label: "Products", href: "/#products" },
  { label: "Our Story", href: "/#story" },
  { label: "Why Maati", href: "/#why" },
  { label: "Recipes", href: "/recipes" },
  { label: "Wholesale", href: "/#wholesale" },
  { label: "Contact", href: "/#contact" },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const renderLink = (l, isMobile = false) => {
    const isHash = l.href.includes("#");
    const cls = isMobile
      ? "block text-base text-mo-ink-soft hover:text-mo-terracotta"
      : "text-sm text-mo-ink-soft hover:text-mo-terracotta transition-colors duration-300 relative group";
    const testId = `${isMobile ? "mobile" : "nav"}-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;

    const inner = (
      <>
        {l.label}
        {!isMobile && (
          <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#B33922] transition-all duration-300 group-hover:w-full" />
        )}
      </>
    );

    // On home page, hash links scroll; otherwise go to / with hash
    if (isHash) {
      const onClick = isMobile ? () => setOpen(false) : undefined;
      if (isHome) {
        const anchor = l.href.replace("/", "");
        return (
          <a key={l.href} href={anchor} data-testid={testId} className={cls} onClick={onClick}>
            {inner}
          </a>
        );
      }
      return (
        <a key={l.href} href={l.href} data-testid={testId} className={cls} onClick={onClick}>
          {inner}
        </a>
      );
    }

    return (
      <Link
        key={l.href}
        to={l.href}
        data-testid={testId}
        className={cls}
        onClick={isMobile ? () => setOpen(false) : undefined}
      >
        {inner}
      </Link>
    );
  };

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F6F0]/90 backdrop-blur-md border-b border-[#5C5248]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3">
          <img
            src={BRAND.logo}
            alt="Maati Organics"
            className="h-12 w-12 object-contain rounded-full bg-white/40 p-1"
          />
          <div className="leading-tight">
            <div className="font-serif-display text-xl text-mo-ink tracking-tight">
              Maati Organics
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-mo-ink-soft">
              Handground · Honest
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => renderLink(l, false))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BRAND.whatsapp.link}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-mo-terracotta text-mo-cream text-sm font-medium hover:bg-[#8F2B17] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            Order on WhatsApp
          </a>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-mo-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F9F6F0] border-t border-[#5C5248]/10 px-6 py-6 space-y-4"
        >
          {LINKS.map((l) => renderLink(l, true))}
          <a
            href={BRAND.whatsapp.link}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-whatsapp-cta"
            className="block text-center px-5 py-3 rounded-full bg-mo-terracotta text-mo-cream text-sm font-medium"
          >
            Order on WhatsApp
          </a>
        </div>
      )}
    </header>
  );
};
